import { ownAmount, type CategoryKind, type Goal, type PeriodItem, type ShareInput, type Transaction } from '@fin/shared';












export type Mode = 'expense' | 'income' | 'debt' | 'transfer';

export interface TxDefaults {
  mode?: Mode;
  walletId?: number;
  toWalletId?: number;
  amount?: number;
  /** a transfer across currencies: what arrives on the receiving wallet */
  amountTo?: number;
  categoryId?: number;
  note?: string;
  periodItemId?: number;
  debtKind?: CategoryKind;
  debtId?: number;
  contactId?: number;
  excludeFromReport?: boolean;
  /** other people's parts the operation is created with (a shared plan line) */
  shares?: ShareInput[];
}

export function repeatDefaults(t: Transaction, counterpartCurrency?: string): TxDefaults {
  const amount = Math.abs(t.amount);
  if (t.transferId) {
    const out = t.amount < 0;
    // the incoming leg is in the receiving currency: the sum sent is on the other leg
    const sent = out ? amount : t.counterpartAmount != null ? Math.abs(t.counterpartAmount) : counterpartCurrency === t.currency ? amount : undefined;
    return {
      mode: 'transfer', amount: sent, note: t.note,
      walletId: out ? t.walletId : t.counterpartWalletId ?? undefined, toWalletId: out ? t.counterpartWalletId ?? undefined : t.walletId,
    };
  }
  const mode = modeOf(t.categoryKind);
  // a payment goes to the same debt; borrowing or lending again opens a new debt record
  const paysDebt = t.categoryKind === 'repayment' || t.categoryKind === 'debt_collection';
  return {
    mode, amount, note: t.note, walletId: t.walletId, categoryId: t.categoryId, contactId: t.contactId ?? undefined,
    excludeFromReport: t.excludeFromReport, ...(mode === 'debt' ? { debtKind: t.categoryKind, debtId: paysDebt ? t.debtId ?? undefined : undefined } : {}),
  };
}

export function planLineDefaults(item: PeriodItem, goals: Goal[]): TxDefaults {
  // the first payment of a shared line is the whole sum: the server moves the line's shares (with the
  // returns already made) to that operation; spent is the owner's part, so a later payment covers the rest of it
  const first = item.spent <= 0;
  const rest = first ? item.amount : Math.max(0, ownAmount(item) - item.spent);
  const base = { amount: rest || item.amount, note: item.name, periodItemId: item.id };
  if (item.walletId) return { ...base, mode: 'transfer', toWalletId: item.walletId };
  const goalWallet = item.goalId ? goals.find((g) => g.id === item.goalId)?.walletId : null;
  if (goalWallet) return { ...base, mode: 'transfer', toWalletId: goalWallet };
  if (item.debtId) return { ...base, mode: 'debt', debtKind: 'repayment', debtId: item.debtId };
  return { ...base, mode: 'expense', categoryId: item.categoryId ?? undefined };
}

export function modeOf(kind: CategoryKind): Mode {
  if (kind === 'income') return 'income';
  if (kind === 'expense') return 'expense';
  if (kind === 'transfer') return 'transfer';
  return 'debt';
}
